import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  Info,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import { AdminSidebar } from '../../components/admin/AdminSidebar';
import {
  getServiceHealth,
  clearServiceIncidentRecord,
  type ServiceHealthReport,
  type ServiceState,
  type SecretState,
  type ServiceStatus,
} from '@shared/services/admin-api';

function formatDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const SERVICE_PILL: Record<ServiceState, { label: string; className: string }> = {
  operational: { label: 'OPERATIONAL', className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  degraded: { label: 'DEGRADED', className: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  down: { label: 'DOWN', className: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

const SECRET_PILL: Record<SecretState, { label: string; className: string }> = {
  configured: { label: 'SET', className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25' },
  placeholder: { label: 'PLACEHOLDER', className: 'bg-amber-500/10 text-amber-400 border-amber-500/25' },
  missing: { label: 'MISSING', className: 'bg-red-500/10 text-red-400 border-red-500/25' },
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }}
      className="shrink-0 flex items-center gap-1.5 rounded-md border border-dark-border bg-dark-bg px-2.5 py-1.5 text-[11px] font-medium text-content-muted transition-colors hover:text-content"
      title="Copy command"
    >
      {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function ServiceCard({
  service,
  incident,
  onCleared,
}: {
  service: ServiceStatus;
  incident: ServiceHealthReport['incidents'][number] | undefined;
  onCleared: () => void;
}) {
  const [clearing, setClearing] = useState(false);
  const pill = SERVICE_PILL[service.state];

  return (
    <div
      className={`rounded-2xl border bg-dark-surface p-5 ${
        service.state === 'down' ? 'border-red-500/40' : 'border-dark-border'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-heading text-[15px] font-bold text-content">{service.label}</h3>
            {service.blocksLaunch && (
              <span className="rounded border border-indigo-400/30 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-300">
                Blocks launch
              </span>
            )}
          </div>
          <p className="mt-1.5 max-w-2xl text-[13px] leading-relaxed text-content-muted">
            {service.state === 'operational' ? 'Powers: ' : 'Currently broken: '}
            {service.impact}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-md border px-2 py-1 text-[11px] font-bold tracking-wider ${pill.className}`}
        >
          {pill.label}
        </span>
      </div>

      {/* Per-secret verdicts. Values are never sent to the client — only these three states. */}
      <div className="mt-4 flex flex-wrap gap-2">
        {service.secrets.map((s) => {
          const sp = SECRET_PILL[s.state];
          return (
            <span
              key={s.name}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-[11px] ${sp.className}`}
            >
              <span className="opacity-70">{s.name}</span>
              <span className="font-sans font-bold">{sp.label}</span>
            </span>
          );
        })}
      </div>

      {service.fixCommand && (
        <div className="mt-4">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-content-dim">
            Fix
          </div>
          <div className="flex items-start gap-2">
            <pre className="min-w-0 flex-1 overflow-x-auto rounded-lg border border-dark-border bg-dark-bg p-3 font-mono text-[11.5px] leading-relaxed text-content-muted">
              {service.fixCommand}
            </pre>
            <CopyButton text={service.fixCommand} />
          </div>
          <p className="mt-1.5 text-[11px] text-content-dim">
            Redeploy functions after setting a secret — a running instance keeps the old value.
          </p>
        </div>
      )}

      {incident && (
        <div className="mt-4 rounded-lg border border-dark-border bg-dark-bg p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-content-dim">
              Last recorded failure
            </span>
            <button
              onClick={async () => {
                setClearing(true);
                try {
                  await clearServiceIncidentRecord(service.id);
                  onCleared();
                } finally {
                  setClearing(false);
                }
              }}
              disabled={clearing}
              className="text-[11px] font-medium text-content-muted underline-offset-2 hover:text-content hover:underline disabled:opacity-50"
            >
              {clearing ? 'Clearing…' : 'Clear record'}
            </button>
          </div>
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-content-muted">{incident.lastReason}</p>
          <p className="mt-1.5 text-[11px] text-content-dim">
            {incident.failureCount} failure{incident.failureCount === 1 ? '' : 's'} · most recent in{' '}
            <span className="font-mono">{incident.lastContext}</span> at {formatDate(incident.lastFailureAt)}
            {' · first seen '}
            {formatDate(incident.firstFailureAt)}
          </p>
        </div>
      )}
    </div>
  );
}

export function AdminSettings() {
  const [report, setReport] = useState<ServiceHealthReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await getServiceHealth());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load service health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const incidentFor = (id: string) => report?.incidents?.find((i) => i.serviceId === id);
  const blocking = report?.services.filter((s) => s.state === 'down') ?? [];
  const degraded = report?.services.filter((s) => s.state === 'degraded') ?? [];
  const healthy = report?.services.filter((s) => s.state === 'operational') ?? [];

  return (
    <div className="min-h-screen bg-dark-bg">
      <div className="flex">
        <AdminSidebar />
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-heading text-2xl font-bold mb-1">Settings</h1>
              <p className="text-content-muted text-sm">
                External-service configuration and system health
              </p>
            </div>
            <button
              onClick={() => void load()}
              disabled={loading}
              className="flex items-center gap-2 rounded-lg border border-dark-border bg-dark-surface px-3 py-2 text-[13px] font-medium text-content-muted transition-colors hover:text-content disabled:opacity-50"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {loading && !report && (
            <div className="rounded-2xl border border-dark-border bg-dark-surface p-10 text-center text-sm text-content-muted">
              Checking every external service…
            </div>
          )}

          {error && (
            <div className="mb-6 flex items-start gap-3 rounded-2xl border border-red-500/30 bg-red-500/10 p-4">
              <XCircle size={18} className="mt-0.5 shrink-0 text-red-400" />
              <div>
                <p className="text-[13px] font-semibold text-red-300">Could not load service health</p>
                <p className="mt-0.5 text-[12.5px] text-content-muted">{error}</p>
              </div>
            </div>
          )}

          {report && (
            <>
              {/* Headline verdict — the thing that was previously invisible. */}
              <div
                className={`mb-6 flex items-start gap-3 rounded-2xl border p-4 ${
                  report.state === 'operational'
                    ? 'border-emerald-500/30 bg-emerald-500/10'
                    : report.state === 'degraded'
                      ? 'border-amber-500/30 bg-amber-500/10'
                      : 'border-red-500/40 bg-red-500/10'
                }`}
              >
                {report.state === 'operational' ? (
                  <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-emerald-400" />
                ) : report.state === 'degraded' ? (
                  <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-400" />
                ) : (
                  <ShieldAlert size={20} className="mt-0.5 shrink-0 text-red-400" />
                )}
                <div className="min-w-0">
                  <p className="font-heading text-[15px] font-bold text-content">
                    {report.state === 'operational'
                      ? 'Every external service is configured'
                      : report.state === 'degraded'
                        ? `${degraded.length} optional service${degraded.length === 1 ? '' : 's'} unconfigured`
                        : `${blocking.length} launch-blocking service${blocking.length === 1 ? '' : 's'} not configured`}
                  </p>
                  <p className="mt-1 text-[13px] leading-relaxed text-content-muted">
                    {report.state === 'down'
                      ? 'These fail silently in production — the scheduled jobs that use them log a success count of zero and look like a quiet day. Nothing will alert you.'
                      : report.state === 'degraded'
                        ? 'The product works, but these features are unavailable.'
                        : 'Nothing is currently blocking launch on configuration grounds.'}
                  </p>
                  <p className="mt-1.5 font-mono text-[11px] text-content-dim">
                    Checked {formatDate(Date.parse(report.generatedAt))}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {[...blocking, ...degraded, ...healthy].map((s) => (
                  <ServiceCard
                    key={s.id}
                    service={s}
                    incident={incidentFor(s.id)}
                    onCleared={() => void load()}
                  />
                ))}
              </div>

              {/* Decision note — JB's call, recorded where the behaviour lives. */}
              <div className="mt-6 flex items-start gap-3 rounded-2xl border border-dark-border bg-dark-surface p-4">
                <Info size={17} className="mt-0.5 shrink-0 text-indigo-400" />
                <div className="text-[12.5px] leading-relaxed text-content-muted">
                  <p className="font-semibold text-content">
                    Your call: what a placeholder key should do at send time
                  </p>
                  <p className="mt-1.5">
                    <span className="text-content">Currently:</span> a send is abandoned{' '}
                    <em>before</em> the network call, and the reason is recorded here. Chosen because
                    calling a provider with a stand-in key returns an opaque 401 that reads like a
                    transient outage — so the real cause stayed invisible for months.
                  </p>
                  <p className="mt-1.5">
                    <span className="text-content">The alternative:</span> attempt every send anyway
                    and record whatever the provider says. That surfaces genuine provider faults
                    (suspended account, domain not verified) that a pre-check cannot see — at the
                    cost of a slower failure and a vaguer message.
                  </p>
                  <p className="mt-1.5">
                    Switching means dropping the <code className="font-mono text-[11.5px]">isServiceConfigured</code>{' '}
                    guard in <code className="font-mono text-[11.5px]">functions/src/email/resendClient.ts</code>.
                    Say the word and it changes.
                  </p>
                </div>
              </div>

              <p className="mt-4 text-[11.5px] leading-relaxed text-content-dim">
                Secret values are never sent to the browser. This page receives only three verdicts
                per secret — set, placeholder, or missing — computed inside the function.
              </p>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
